<script lang="ts">
	import { onDestroy } from 'svelte';
	import maplibregl from 'maplibre-gl';
	import type { FeatureCollection } from 'geojson';
	import { client } from '@motis-project/motis-client';
	import GeoJSON from '$lib/map/GeoJSON.svelte';
	import Layer from '$lib/map/Layer.svelte';

	let {
		map,
		visible = true,
		time
	}: {
		map: maplibregl.Map | undefined;
		visible?: boolean;
		// Closures in effect at this time are shown, so the map matches the
		// departure / arrival time of the search.
		time?: Date;
	} = $props();

	const UPDATE_INTERVAL_MS = 300_000;
	const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

	let closures = $state<FeatureCollection>(EMPTY);

	const update = async () => {
		try {
			const at = time ? `?time=${encodeURIComponent(time.toISOString())}` : '';
			const response = await fetch(
				`${client.getConfig().baseUrl}/api/experimental/map/osm-rt${at}`
			);
			if (response.ok) {
				closures = (await response.json()) as FeatureCollection;
			}
		} catch {
			// keep the closures of the previous update
		}
	};

	$effect(() => {
		void time;
		update();
	});
	const timer = setInterval(update, UPDATE_INTERVAL_MS);
	onDestroy(() => clearInterval(timer));

	let popup: maplibregl.Popup | null = null;

	const showPopup = (
		e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] },
		m: maplibregl.Map
	) => {
		const p = e.features?.[0]?.properties;
		if (!p) {
			return;
		}
		const esc = (s: unknown) =>
			String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		popup?.remove();
		popup = new maplibregl.Popup({ closeButton: true })
			.setLngLat(e.lngLat)
			.setHTML(
				`<div class="text-sm">` +
					`<div class="font-bold">${esc(p.key)} = ${esc(p.value)}</div>` +
					`<div>feed: ${esc(p.feed)}</div>` +
					`<div>id: ${esc(p.id)}</div>` +
					`<div>${
						p.matched !== true && p.matched !== 'true'
							? 'not matched to the street graph'
							: p.permanent === true || p.permanent === 'true'
								? 'closed at all times (opening hours not applied)'
								: `closed on ${p.ways} way(s) while its opening hours apply`
					}</div>` +
					`</div>`
			)
			.addTo(m);
	};

	onDestroy(() => popup?.remove());

	const cursor = (m: maplibregl.Map, style: string) => {
		m.getCanvas().style.cursor = style;
	};
</script>

{#if map && visible}
	<GeoJSON id="osm-rt" data={closures}>
		<!-- Closed while their opening hours apply. -->
		<Layer
			id="osm-rt-closures"
			type="line"
			filter={['all', ['==', ['get', 'matched'], true], ['!=', ['get', 'permanent'], true]]}
			layout={{ 'line-cap': 'round', 'line-join': 'round' }}
			paint={{
				'line-color': '#dc2626',
				'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 14, 6, 18, 10],
				'line-opacity': 0.8
			}}
			onclick={showPopup}
			onmouseenter={(_, m) => cursor(m, 'pointer')}
			onmouseleave={(_, m) => cursor(m, '')}
		/>
		<!-- Matched, but the opening hours are not applied: closed at all times. -->
		<Layer
			id="osm-rt-closures-permanent"
			type="line"
			filter={['all', ['==', ['get', 'matched'], true], ['==', ['get', 'permanent'], true]]}
			layout={{ 'line-cap': 'round', 'line-join': 'round' }}
			paint={{
				'line-color': '#f59e0b',
				'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 14, 6, 18, 10],
				'line-opacity': 0.8
			}}
			onclick={showPopup}
			onmouseenter={(_, m) => cursor(m, 'pointer')}
			onmouseleave={(_, m) => cursor(m, '')}
		/>
		<!-- In the feed, but not matched to the street graph: not routed around. -->
		<Layer
			id="osm-rt-closures-unmatched"
			type="line"
			filter={['!=', ['get', 'matched'], true]}
			layout={{ 'line-cap': 'butt', 'line-join': 'round' }}
			paint={{
				'line-color': '#94a3b8',
				'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 14, 3, 18, 5],
				'line-opacity': 0.7,
				'line-dasharray': [2, 2]
			}}
			onclick={showPopup}
			onmouseenter={(_, m) => cursor(m, 'pointer')}
			onmouseleave={(_, m) => cursor(m, '')}
		/>
	</GeoJSON>
{/if}
